import {useBreadcrumb, useResource, useRouterType, useTranslate} from '@refinedev/core';
import {type CreateButtonProps, type ListProps} from '@refinedev/mui';
import {FC, ReactNode} from 'react';

import {CreateButton} from '@components/buttons/create';
import {CustomBreadcrumbs} from '@minimal/components/custom-breadcrumbs';

type CustomListProps = Omit<ListProps, 'title'> & {
  title?: string | ReactNode;
  hideBreadcrumbs?: boolean;
};

export const CustomList: FC<CustomListProps> = ({
  title,
  canCreate,
  children,
  createButtonProps: createButtonPropsFromProps,
  resource: resourceFromProps,
  headerButtons,
  hideBreadcrumbs,
}) => {
  const translate = useTranslate();

  const routerType = useRouterType();

  const {resource, identifier} = useResource(resourceFromProps);

  const isCreateButtonVisible =
    canCreate ?? ((resource?.canCreate ?? !!resource?.create) || createButtonPropsFromProps);

  const {breadcrumbs} = useBreadcrumb();

  const createButtonProps: CreateButtonProps | undefined = isCreateButtonVisible
    ? {
        resource: routerType === 'legacy' ? resource?.route : identifier,
        ...createButtonPropsFromProps,
      }
    : undefined;

  const defaultHeaderButtons = isCreateButtonVisible ? <CreateButton {...createButtonProps} /> : null;

  return (
    <>
      <CustomBreadcrumbs
        heading={title?.toString() ?? translate(`${resource?.meta?.prefix}.titles.list`)}
        links={
          hideBreadcrumbs
            ? []
            : breadcrumbs.map((item) => ({
                ...item,
                name: item.label,
              }))
        }
        action={
          headerButtons
            ? typeof headerButtons === 'function'
              ? headerButtons({
                  defaultButtons: defaultHeaderButtons,
                  createButtonProps,
                })
              : headerButtons
            : defaultHeaderButtons
        }
        sx={{mb: {xs: 3, md: 5}}}
      />
      {children}
    </>
  );
};
